
import React from 'react';
import { User } from './types';
import { Button } from "@/components/ui/button";
import { UserX, Shield, ShieldOff } from "lucide-react";

interface UserTableProps {
  users: User[];
  isLoading: boolean;
  toggleAdminStatus: (userId: string, currentStatus: boolean) => Promise<void>;
}

const UserTable: React.FC<UserTableProps> = ({ users, isLoading, toggleAdminStatus }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b">
            <th className="text-left py-3 px-2 font-medium">Name</th>
            <th className="text-left py-3 px-2 font-medium">Email</th>
            <th className="text-left py-3 px-2 font-medium">Status</th>
            <th className="text-left py-3 px-2 font-medium">Join Date</th>
            <th className="text-left py-3 px-2 font-medium">Admin</th>
            <th className="text-left py-3 px-2 font-medium">Progress</th>
            <th className="text-right py-3 px-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.length > 0 ? (
            users.map((user) => (
              <tr key={user.id} className="border-b hover:bg-gray-50">
                <td className="py-3 px-2">{user.name}</td>
                <td className="py-3 px-2">{user.email}</td>
                <td className="py-3 px-2">
                  <span className={`inline-block rounded-full px-2 py-1 text-xs ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' :
                    user.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="py-3 px-2">{user.joinDate}</td>
                <td className="py-3 px-2">
                  <span className={`inline-block rounded-full px-2 py-1 text-xs ${
                    user.isAdmin ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {user.isAdmin ? 'Admin' : 'User'}
                  </span>
                </td>
                <td className="py-3 px-2">
                  <div className="flex items-center">
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2">
                      <div className="bg-hormony-coral h-2.5 rounded-full" style={{ width: `${(user.progress / 30) * 100}%` }}></div>
                    </div>
                    <span className="text-xs whitespace-nowrap">Day {user.progress}/30</span>
                  </div>
                </td>
                <td className="py-3 px-2 text-right">
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => toggleAdminStatus(user.id, user.isAdmin)}
                      className={user.isAdmin ? "text-purple-500 hover:text-purple-700" : "text-gray-500 hover:text-gray-700"}
                      title={user.isAdmin ? "Revoke admin status" : "Grant admin status"}
                    >
                      {user.isAdmin ? (
                        <ShieldOff className="h-4 w-4" />
                      ) : (
                        <Shield className="h-4 w-4" />
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      title="Remove user"
                    >
                      <UserX className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="py-4 text-center text-muted-foreground">
                {isLoading ? 'Loading users...' : 'No users found matching your search criteria.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;
